import theme, {
  TRANSITION_DURATION,
  TRANSITION_EASING,
} from '@adsk/alloy-react-theme';

import {
  getBadgeTokensAlloy,
  getBannerTokensAlloy,
  getBoxComponentTokensAlloy,
  getBreadcrumbsTokensAlloy,
  getCheckboxTokensAlloy,
  getColorPickerTokensAlloy,
  getEmptyStateTokensAlloy,
  getDropdownTokensAlloy,
  getFooterTokensAlloy,
  getFormFieldTokensAlloy,
  getInlineEditTokensAlloy,
  getMenuComponentTokensAlloy,
  getModalComponentTokensAlloy,
  getPageHeaderTokensAlloy,
  getPanelTokensAlloy,
  getTextareaTokensAlloy,
  getToggleTokensAlloy,
  getTreeTokensAlloy,
  getRadioButtonTokensAlloy,
} from './componentTokens';
import { ALLOY_THEME_ID } from './constants';
import { type WeaveTheme } from './types';

const splitBoxShadowTheme = (boxShadowTheme: string) => {
  // Split by spaces but preserve color values that contain spaces
  const parts = boxShadowTheme.split(' ');
  const [x, y, blur, spread] = parts.slice(0, 4);
  // Join remaining parts as the color (handles rgba values with spaces)
  const color = parts.slice(4).join(' ');

  return {
    'box-shadow-x': { focus: x },
    'box-shadow-y': { focus: y },
    'box-shadow-blur': { focus: blur },
    'box-shadow-spread': { focus: spread },
    'box-shadow-color': { focus: color },
  };
};

const spacing = {
  '2': '2px',
  '4': '4px',
  '8': '8px',
  '10': '10px',
  '12': '12px',
  '16': '16px',
  '20': '20px',
  '24': '24px',
  '32': '32px',
  '38': '38px',
  '48': '48px',
  '64': '64px',
  '96': '96px',
  '128': '128px',
  '256': '256px',
  none: '0',
  xs: '4px',
  s: '8px',
  sm: '12px',
  l: '24px',
  xxxl: '48px',
};

export const alloyTheme = {
  id: ALLOY_THEME_ID,
  tokens: {
    generic: {
      spacing,
    },
    component: {
      badge: getBadgeTokensAlloy(theme),
      a: {
        'text-color': {
          hover: theme.tokens.colors.text.hover.value,
          default: theme.tokens.colors.text.link.value,
          pressed: theme.tokens.colors.text.pressed.value,
        },
        'text-decoration': undefined,
        'text-decoration-thickness': undefined,
        'box-shadow': undefined,
      },
      accordion: {
        'background-color': theme.tokens.colors.background.secondary.value,
        'box-shadow-inset': {
          focus: undefined,
        },
        ...splitBoxShadowTheme(theme.halos.focus),
        'font-family': theme.typography.labelSmall.fontFamily,
        'font-weight': theme.typography.labelSmall.fontWeight,
        'font-size': theme.typography.labelSmall.fontSize,
        'line-height': theme.typography.labelSmall.lineHeight,
        height: '32px',
        icon: {
          width: '16px',
          size: 16,
          'margin-left': spacing.s,
          'transition-property': 'transform',
          'transition-duration': '0.15s',
          'transition-timing-function': 'linear',
          'transform-expanded': 'rotate(0deg)',
          'transform-collapsed': 'rotate(-90deg)',
          fill: undefined,
        },
        'padding-bottom': undefined,
        'padding-left': undefined,
        'padding-right': undefined,
        'padding-top': undefined,
        'margin-top': spacing[2],
        'text-color': theme.tokens.colors.text.primary.value,
        secondary: {
          'text-color': theme.tokens.colors.text.secondary.value,
        },
        label: {
          'margin-top': spacing.xs,
          'margin-bottom': spacing.xs,
          'margin-left': spacing.xs,
        },
      },
      contentloader: {
        'background-color': theme.tokens.colors.skeleton.shimmer.one.value,
        'foreground-color': theme.tokens.colors.skeleton.shimmer.two.value,
      },
      divider: {
        color: {
          subtle: theme.tokens.colors.border.subtler.value,
          default: theme.tokens.colors.border.subtle.value,
          strong: theme.tokens.colors.border.value,
        },
      },
      emptystate: getEmptyStateTokensAlloy(theme),
      avatar: {
        '1': {
          'background-color':
            theme.tokens.colors.background.accent.one.subtle.value,
        },
        '2': {
          'background-color': theme.tokens.colors.background.accent.two.value,
        },
        '3': {
          'background-color':
            theme.tokens.colors.background.accent.two.subtle.value,
        },
        '4': {
          'background-color':
            theme.tokens.colors.background.accent.four.subtle.value,
        },
        '5': {
          'background-color':
            theme.tokens.colors.background.accent.three.subtle.value,
        },
        '6': {
          'background-color':
            theme.tokens.colors.background.accent.five.subtler.value,
        },
        '7': {
          'background-color':
            theme.tokens.colors.background.accent.five.subtle.value,
        },
        'border-radius': theme.borderRadius.circle,
        'font-family': theme.typography.bodyMedium.fontFamily,
        'font-weight': theme.typography.bodyMedium.fontWeight,
        'text-color': theme.tokens.colors.text.primary.value,
        border: {
          color: theme.tokens.colors.border.inverse.value,
        },
        xs: {
          'font-size': '11px',
          'line-height': '16px',
        },
        s: {
          'font-size': '12px',
          'line-height': '18px',
        },
        m: {
          'font-size': '16px',
          'line-height': '20px',
        },
        l: {
          'font-size': '24px',
          'line-height': '30px',
        },
        xl: {
          'font-size': '32px',
          'line-height': '38px',
        },
        xxl: {
          'font-size': '56px',
          'line-height': '64px',
        },
        default: {
          'icon-color': theme.tokens.colors.icon.tertiary.value,
          'background-color':
            theme.tokens.colors.background.accent.six.subtler.value,
        },
      },
      progressring: {
        'icon-color': {
          default: theme.tokens.colors.icon.load.value,
          inverse: theme.tokens.colors.icon.inverse.value,
        },
        'icon-color-light': {
          default: theme.tokens.colors.icon.load.value,
          inverse: theme.tokens.colors.icon.inverse.value,
        },
      },
      button: {
        'border-radius': '2px',
        'border-width': '1px',
        'font-family': theme.typography.labelMedium.fontFamily,
        'font-weight': theme.typography.labelMedium.fontWeight,
        solid: {
          'background-color': theme.tokens.colors.background.brand.value,
          'border-color': 'transparent',
          'text-color': theme.tokens.colors.text.inverse.value,
          icon: { fill: theme.tokens.colors.icon.inverse.value },
          hover: {
            'background-color':
              theme.tokens.colors.background.brand.hover.value,
          },
          pressed: {
            'background-color':
              theme.tokens.colors.background.brand.pressed.value,
          },
          selected: {
            'background-color':
              theme.tokens.colors.background.brand.pressed.value,
          },
          disabled: {
            'background-color':
              theme.tokens.colors.background.neutral.inactive.value,
            'text-color': theme.tokens.colors.text.inactive.value,
            icon: { fill: theme.tokens.colors.icon.inactive.value },
          },
          loading: {
            'background-color':
              theme.tokens.colors.background.brand.inactive.value,
          },
          alert: {
            'background-color': theme.tokens.colors.background.error.value,
            'text-color': theme.tokens.colors.text.error.inverse.value,
            pressed: {
              'background-color':
                theme.tokens.colors.background.error.bold.value,
            },
            loading: {
              'background-color':
                theme.tokens.colors.background.error.inactive.value,
            },
            selected: {
              'background-color':
                theme.tokens.colors.background.error.selected.value,
            },
          },
        },
        outlined: {
          'border-color': theme.tokens.colors.border.value,
          'text-color': theme.tokens.colors.text.primary.value,
          icon: { fill: theme.tokens.colors.icon.secondary.value },
          hover: {
            icon: { fill: theme.tokens.colors.icon.hover.value },
          },
          pressed: {
            'border-color': theme.tokens.colors.border.pressed.value,
            icon: { fill: theme.tokens.colors.icon.pressed.value },
          },
          focus: {
            icon: { fill: theme.tokens.colors.icon.pressed.value },
          },
          active: {
            icon: { fill: theme.tokens.colors.icon.selected.value },
          },
          selected: {
            'background-color':
              theme.tokens.colors.background.neutral.selected.value,
          },
          disabled: {
            'border-color': theme.tokens.colors.border.inactive.value,
            'text-color': theme.tokens.colors.text.inactive.value,
            icon: { fill: theme.tokens.colors.icon.inactive.value },
          },
          loading: {
            'border-color': theme.tokens.colors.border.inactive.value,
            'text-color': theme.tokens.colors.text.inactive.value,
          },
          alert: {
            'text-color': theme.tokens.colors.text.error.value,
            icon: { fill: theme.tokens.colors.icon.error.bold.value },
          },
        },
        flat: {
          'border-color': 'transparent',
          'text-color': theme.tokens.colors.text.link.value,
          icon: { fill: theme.tokens.colors.icon.link.value },
          hover: {
            'background-color':
              theme.tokens.colors.background.neutral.hover.value,
            'text-color': theme.tokens.colors.text.hover.value,
            icon: { fill: theme.tokens.colors.icon.hover.value },
          },
          pressed: {
            'background-color':
              theme.tokens.colors.background.neutral.pressed.value,
            'text-color': theme.tokens.colors.text.pressed.value,
            icon: { fill: theme.tokens.colors.icon.pressed.value },
          },
          selected: {
            'background-color':
              theme.tokens.colors.background.neutral.selected.value,
          },
          disabled: {
            'text-color': theme.tokens.colors.text.inactive.value,
            icon: { fill: theme.tokens.colors.icon.inactive.value },
          },
          loading: {
            'text-color': theme.tokens.colors.text.inactive.value,
          },
          alert: {
            'text-color': theme.tokens.colors.text.error.value,
            icon: { fill: theme.tokens.colors.icon.error.bold.value },
          },
        },
        small: {
          height: '24px',
          'font-size': theme.typography.labelSmall.fontSize,
          'line-height': theme.typography.labelSmall.lineHeight,
          'padding-text': '0 7px',
          'padding-icon': '0 3px',
          'padding-icon-text': '0 7px 0 5px',
          'icon-size': 16,
        },
        medium: {
          height: '36px',
          'font-size': theme.typography.labelMedium.fontSize,
          'line-height': theme.typography.labelMedium.lineHeight,
          'padding-text': '0 15px',
          'padding-icon': '0 5px',
          'padding-icon-text': '0 15px 0 11px',
          'icon-size': 24,
        },
        'box-shadow': {
          focus: theme.halos.focus,
          hover: theme.halos.hover,
          pressed: theme.halos.active,
        },
        'icon-margin': theme.spacing.XS,
        link: {
          small: theme.typography.bodySmall,
          medium: theme.typography.bodyMedium,
        },
      },
      typography: {
        ...theme.typography,
      },
      highlighttext: {
        'background-color': theme.tokens.colors.background.highlight.value,
      },
      input: {
        'min-height': '36px',
        'border-top-left-radius': `${theme.borderRadius.small}px`,
        'border-top-right-radius': `${theme.borderRadius.small}px`,
        'border-bottom-left-radius': `${theme.borderRadius.small}px`,
        'border-bottom-right-radius': `${theme.borderRadius.small}px`,
        'border-bottom-color': {
          focus: theme.tokens.colors.border.focused.value,
          hover: theme.tokens.colors.border.value, // same as default - no visual change
        },
        placeholder: {
          'text-color': theme.tokens.colors.text.tertiary.value,
        },
        box: {
          'background-color': theme.tokens.colors.background.primary.value,
          'border-color': theme.tokens.colors.border.value,
          'border-style': 'solid',
          'border-width': '1px',
          'padding-bottom': '8px',
          'padding-left': '12px',
          'padding-right': '12px',
          'padding-top': '8px',
        },
        error: {
          'border-bottom-color': theme.tokens.colors.border.error.value,
          icon: {
            fill: theme.tokens.colors.text.error.value,
          },
        },
        'font-family': theme.typography.labelMedium.fontFamily,
        'font-weight': theme.typography.labelMedium.fontWeight,
        'font-size': theme.typography.labelMedium.fontSize,
        'line-height': theme.typography.labelMedium.lineHeight,
        'text-color': theme.tokens.colors.text.primary.value,
        opacity: {
          disabled: 0.4,
        },
        'transition-duration': TRANSITION_DURATION,
        'transition-property': {
          border: 'border',
          'box-shadow': 'box-shadow',
          'transition-property': 'border, box-shadow',
        },
        'transition-timing-function': TRANSITION_EASING,
        disabled: {
          'background-color':
            theme.tokens.colors.background.neutral.inactive.value,
          'text-color': theme.tokens.colors.text.inactive.value,
          'border-color': theme.tokens.colors.border.inactive.value,
        },
      },
      overlay: {
        light: {
          'background-color': theme.tokens.colors.background.primary.value,
          'text-color': theme.tokens.colors.text.primary.value,
          'border-color': theme.tokens.colors.background.primary.value,
          'border-radius': theme.borderRadius.medium,
          'box-shadow': theme.shadows.highElevation,
        },
        medium: {
          'background-color':
            theme.tokens.colors.background.inverse.selected.value,
          'text-color': theme.tokens.colors.text.primary.value,
          'border-color': theme.tokens.colors.background.inverse.selected.value,
          'border-radius': theme.borderRadius.medium,
          'box-shadow': theme.shadows.lowElevation,
        },
        dark: {
          'background-color':
            theme.tokens.colors.background.inverse.subtle.value,
          'text-color': theme.tokens.colors.text.inverse.value,
          'border-color': theme.tokens.colors.background.inverse.subtle.value,
          'border-radius': theme.borderRadius.small,
          'link-color': theme.tokens.colors.text.link.inverse.value,
        },
        default: {
          'background-color':
            theme.tokens.colors.background.inverse.subtle.value,
          'text-color': theme.tokens.colors.text.inverse.value,
          'border-color': theme.tokens.colors.background.inverse.subtle.value,
          'border-radius': theme.borderRadius.small,
          'link-color': theme.tokens.colors.text.link.inverse.value,
        },
      },
      tabs: {
        'font-family': theme.typography.heading4.fontFamily,
        'font-weight': theme.typography.bodyMedium.fontWeight,
        'font-size': theme.typography.heading4.fontSize,
        'line-height': theme.typography.heading4.lineHeight,

        'margin-horizontal': spacing.sm,
        'margin-bottom': undefined,
        'padding-top': '0px',
        'padding-vertical': '6px',
        'padding-horizontal': spacing.xs,
        'padding-bottom': '6px',
        'icon-margin': spacing.xs,
        'min-width': '48px',

        'border-bottom': undefined,

        default: {
          'text-color': theme.tokens.colors.text.primary.value,
          'icon-color': theme.tokens.colors.text.primary.value,
        },

        hover: {
          'text-color': theme.tokens.colors.text.hover.value,
          'icon-color': theme.tokens.colors.text.hover.value,
        },

        focus: {
          'text-color': theme.tokens.colors.text.primary.value,
          'icon-color': theme.tokens.colors.text.primary.value,
        },

        active: {
          'text-color': theme.tokens.colors.text.primary.value,
          'icon-color': theme.tokens.colors.text.primary.value,
          'font-weight': theme.typography.heading4.fontWeight,
        },

        disabled: {
          'text-color': theme.tokens.colors.text.inactive.value,
          'icon-color': theme.tokens.colors.icon.inactive.value,
        },

        indicator: {
          'border-color': theme.tokens.colors.border.selected.value,
          'border-width': '2px',
        },

        container: {
          'border-bottom-color': theme.tokens.colors.border.subtle.value,
          'border-bottom-width': '1px',
        },

        'focus-shadow': {
          color: theme.colors.focusHalo,
          width: '2px',
          offset: '-3px',
        },
      },
      box: getBoxComponentTokensAlloy(theme),
      searchfield: {
        icon: {
          color: theme.tokens.colors.icon.tertiary.value,
        },
        clearButton: {
          icon: {
            fill: theme.tokens.colors.icon.secondary.value,
          },
        },
        rightAdornment: {
          gap: `${theme.spacing.XS}px`,
        },
      },
      banner: getBannerTokensAlloy(theme),
      breadcrumbs: getBreadcrumbsTokensAlloy(theme),
      checkbox: getCheckboxTokensAlloy(
        theme,
        TRANSITION_DURATION,
        TRANSITION_EASING,
      ),
      radioButton: getRadioButtonTokensAlloy(
        theme,
        TRANSITION_DURATION,
        TRANSITION_EASING,
      ),
      textarea: getTextareaTokensAlloy(theme),
      toggle: getToggleTokensAlloy(
        theme,
        TRANSITION_DURATION,
        TRANSITION_EASING,
      ),
      colorPicker: getColorPickerTokensAlloy(theme),
      formField: getFormFieldTokensAlloy(theme),
      inlineEdit: getInlineEditTokensAlloy(theme),
      dropdown: getDropdownTokensAlloy(theme),
      footer: getFooterTokensAlloy(theme),
      pageHeader: getPageHeaderTokensAlloy(theme),
      menu: getMenuComponentTokensAlloy(theme),
      modal: getModalComponentTokensAlloy(
        theme,
        theme.typography.bodyMedium.fontFamily,
      ),
      panel: getPanelTokensAlloy(theme),
      tree: getTreeTokensAlloy(theme),
    },
    semantic: {
      halo: {
        default: {
          focus: theme.halos.focus,
          hover: theme.halos.hover,
          pressed: theme.halos.active,
        },
      },
    },
  },
} as unknown as Partial<WeaveTheme>;
